import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import DataTable from '../components/DataTable';
import PermissionForm from '../components/PermissionForm';

const PermissionsPage = () => {
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selectedPermission, setSelectedPermission] = useState(null);

  useEffect(() => {
    fetchPermissions();
  }, []);

  const fetchPermissions = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('permissions')
        .select('*')
        .order('resource')
        .order('action');
      
      if (!error) setPermissions(data || []);
    } catch (error) {
      console.error('Erreur chargement permissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePermission = async (permData) => {
    try {
      const { error } = await supabase
        .from('permissions')
        .insert([{
          name: permData.name,
          resource: permData.resource,
          action: permData.action,
          description: permData.description,
          created_at: new Date().toISOString()
        }]);

      if (error) throw error;

      alert('✅ Permission créée avec succès!');
      fetchPermissions();
      setShowForm(false);
    } catch (error) {
      console.error('Erreur création:', error);
      alert('Erreur: ' + error.message);
    }
  };

  const handleUpdatePermission = async (permData) => {
    try {
      const { error } = await supabase
        .from('permissions')
        .update({
          name: permData.name,
          resource: permData.resource,
          action: permData.action,
          description: permData.description,
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedPermission.id);

      if (error) throw error;

      alert('✅ Permission mise à jour!');
      fetchPermissions();
      setShowForm(false);
      setSelectedPermission(null);
    } catch (error) {
      console.error('Erreur mise à jour:', error);
      alert('Erreur: ' + error.message);
    }
  };

  const handleDeletePermission = async (id) => {
    if (!window.confirm('Supprimer cette permission ?')) return;

    try {
      const { error } = await supabase
        .from('permissions')
        .delete()
        .eq('id', id);

      if (error) throw error;

      alert('✅ Permission supprimée');
      fetchPermissions();
    } catch (error) {
      console.error('Erreur suppression:', error);
      alert('Erreur: ' + error.message);
    }
  };

  const getActionColor = (action) => {
    const colors = {
      'create': 'bg-green-100 text-green-800',
      'read': 'bg-blue-100 text-blue-800',
      'update': 'bg-yellow-100 text-yellow-800',
      'delete': 'bg-red-100 text-red-800'
    };
    return colors[action] || 'bg-gray-100 text-gray-800';
  };

  const columns = [
    {
      key: 'name',
      label: 'Nom',
      render: (perm) => (
        <span className="font-medium text-gray-900">{perm.name}</span>
      )
    },
    {
      key: 'resource',
      label: 'Ressource',
      render: (perm) => (
        <span className="px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800">
          {perm.resource}
        </span>
      )
    },
    {
      key: 'action',
      label: 'Action',
      render: (perm) => (
        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getActionColor(perm.action)}`}>
          {perm.action}
        </span>
      )
    },
    {
      key: 'description',
      label: 'Description',
      render: (perm) => (
        <span className="text-sm text-gray-600">{perm.description || '-'}</span>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (perm) => (
        <div className="flex space-x-2">
          <button
            onClick={() => {
              setSelectedPermission(perm);
              setShowForm(true);
            }}
            className="text-blue-600 hover:text-blue-800 p-1.5 rounded-full hover:bg-blue-50"
            title="Modifier"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
            </svg>
          </button>
          <button
            onClick={() => handleDeletePermission(perm.id)}
            className="text-red-600 hover:text-red-800 p-1.5 rounded-full hover:bg-red-50"
            title="Supprimer"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
            </svg>
          </button>
        </div>
      )
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-8">
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Gestion des permissions
            </h1>
            <p className="text-gray-600 text-lg">
              {permissions.length} permissions disponibles dans le système
            </p>
          </div>
          <button
            onClick={() => {
              setSelectedPermission(null);
              setShowForm(true);
            }}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 flex items-center"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
            </svg>
            Nouvelle permission
          </button>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-6">
            <DataTable data={permissions} columns={columns} />
          </div>
        </div>
      </div>

      {showForm && (
        <PermissionForm
          permission={selectedPermission}
          onClose={() => {
            setShowForm(false);
            setSelectedPermission(null);
          }}
          onSave={selectedPermission ? handleUpdatePermission : handleCreatePermission}
        />
      )}
    </div>
  );
};

export default PermissionsPage;
